import { createContext, useContext, useState, useCallback, useMemo } from 'react';

/**
 * Player-side context — handles player session state plus the player-only
 * light/dark theme that scopes via `.player-light` / `.player-dark` classes
 * on the PlayerShell root (rather than `<html>`).
 *
 * 'system' theme mode was removed Feb 2026 — it called `matchMedia()` on
 * every render which could resolve differently between paints, producing
 * the half-light/half-dark flash the user reported. The mode is now
 * strictly user-controlled with 'light' as the default.
 */
const PlayerContext = createContext(null);

export const usePlayer = () => {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error('usePlayer must be used within PlayerProvider');
  return ctx;
};

export const PlayerProvider = ({ children }) => {
  const [room, setRoom] = useState(null);
  const [playerName, setPlayerName] = useState(() => localStorage.getItem('fp_player_name') || '');
  const [entryId, setEntryId] = useState(() => localStorage.getItem('fp_entry_id') || '');
  const [playerTheme, setPlayerTheme] = useState(() => {
    const stored = localStorage.getItem('fp_player_theme');
    // Migration: anything that's not explicitly 'dark' becomes 'light'.
    // Catches legacy 'system' values stored before we removed that option.
    return stored === 'dark' ? 'dark' : 'light';
  });

  const savePlayerName = useCallback((name) => {
    setPlayerName(name);
    localStorage.setItem('fp_player_name', name);
  }, []);

  const saveEntryId = useCallback((id) => {
    setEntryId(id);
    localStorage.setItem('fp_entry_id', id);
  }, []);

  const setPlayerThemeMode = useCallback((mode) => {
    // Defensive: any legacy 'system' caller falls back to 'light' instead
    // of crashing or producing the half-rendered flash bug.
    const next = mode === 'dark' ? 'dark' : 'light';
    setPlayerTheme(next);
    localStorage.setItem('fp_player_theme', next);
  }, []);

  const togglePlayerTheme = useCallback(() => {
    setPlayerTheme(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('fp_player_theme', next);
      return next;
    });
  }, []);

  const value = useMemo(() => ({
    room, setRoom,
    playerName, savePlayerName,
    entryId, saveEntryId,
    // `playerTheme` and `playerThemeMode` are identical now that 'system'
    // is gone — kept distinct for backwards-compat with components that
    // imported one or the other.
    playerTheme, playerThemeMode: playerTheme,
    setPlayerThemeMode, togglePlayerTheme,
  }), [room, playerName, savePlayerName, entryId, saveEntryId, playerTheme, setPlayerThemeMode, togglePlayerTheme]);

  return (
    <PlayerContext.Provider value={value}>
      {children}
    </PlayerContext.Provider>
  );
};
